import useDebouncedValue from "@pathwright/ui/src/components/hooks/useDebouncedValue"
import uniq from "lodash/uniq"
import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import useControlledQueryParams, {
  decodeQueryParams
} from "../../utils/useControlledQueryParams"

// NOTE: handling author and category filtering by name values NOT ids
// this is because Algolia filters by name values and does not provide facet id
const useControlledLibraryQueryParams = () => {
  // debounced search
  const [search, debouncedSearch, debouncingSearch, handleDebounceSearch] =
    useDebouncedValue(200, null)

  // author names for algolia search
  const [selectedAuthorNames, setSelectedAuthorNames] = useState<string[]>([])
  // category names for algolia search
  const [selectedCategoryNames, setSelectedCategoryNames] = useState<string[]>(
    []
  )

  // useMemo necessary for avoiding infinite loop of setState, probably
  // avoidable by optimizing useControlledQueryParams, but either way
  // useMemo here is probably more efficient.
  const paramsObj = useMemo(
    () => ({
      author: selectedAuthorNames,
      category: selectedCategoryNames,
      search: debouncedSearch
    }),
    [selectedAuthorNames, selectedCategoryNames, debouncedSearch]
  )

  const controlledQueryParams = useControlledQueryParams(paramsObj)

  // Targeted refresh: when navigating INTO the Library root route, re-sync state from URL
  // This is to ensure we apply the query params from the URL to the state.
  const location = useLocation()
  const prevPathnameRef = useRef(location.pathname)
  useEffect(() => {
    const pathname = location.pathname
    const isLibraryRoot = pathname === "/library/" || pathname === "/library"
    const wasLibraryRoot =
      prevPathnameRef.current === "/library/" ||
      prevPathnameRef.current === "/library"

    if (isLibraryRoot && !wasLibraryRoot) {
      const decoded = decodeQueryParams({
        search: debouncedSearch,
        author: selectedAuthorNames,
        category: selectedCategoryNames
      }) as unknown as {
        search: string | null
        author: string[] | null
        category: string[] | null
      }

      const searchQueryParam = decoded.search || null
      const authorFromUrl = decoded.author || []
      const categoryFromUrl = decoded.category || []

      if (search !== searchQueryParam) {
        // Allow URL to override when different
        handleDebounceSearch(search || searchQueryParam)
      }
      if (uniq([...authorFromUrl, ...selectedAuthorNames]).length) {
        setSelectedAuthorNames(authorFromUrl)
      }
      if (uniq([...categoryFromUrl, ...selectedCategoryNames]).length) {
        setSelectedCategoryNames(categoryFromUrl)
      }
    }

    prevPathnameRef.current = pathname
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    // Prevents glitch when clearing search via "View full Library"
    if (debouncingSearch) return

    const {
      search: searchQueryParam,
      author,
      category
    } = controlledQueryParams as {
      search: string
      author: string[]
      category: string[]
    }

    if (search !== searchQueryParam) {
      // Allow searchQueryParam to override search if search is falsey, for instance,
      // if on first mount.
      handleDebounceSearch(search || searchQueryParam)
    }
    // only update selectedAuthorNames if authors in query params differs from local state
    if (uniq([...author, ...selectedAuthorNames]).length) {
      setSelectedAuthorNames(author || [])
    }
    // only update selectedCategoryNames if categories in query params differs from local state
    if (uniq([...category, ...selectedCategoryNames]).length) {
      setSelectedCategoryNames(category || [])
    }
  }, [controlledQueryParams])

  return {
    search,
    debouncedSearch,
    debouncingSearch,
    handleDebounceSearch,
    selectedAuthorNames,
    setSelectedAuthorNames,
    selectedCategoryNames,
    setSelectedCategoryNames,
    controlledQueryParams
  }
}

export default useControlledLibraryQueryParams
